/*  ProductPage — marketing landing for Night Guard / Anti-Snoring / Sports Guard.
    ────────────────────────────────────────────────────────────────
    iter100.122 — Sits at `/products/:slug`. Fully CMS-driven — pulls
    every string + image from the `nav_products` page keys. The four
    products are on the mega-menu; three of them route to this page
    (Retainer keeps its dedicated `/infinite` page which is richer).

    Structure:
      • Left column: hero title + tagline + blurb + benefits chips + image
      • Right column: `ProductEnquiryForm` — the whole point of the page

    Benefits chips per product are hardcoded here (short, evergreen)
    since they're structural, not marketing copy. If you want them
    CMS-controlled later, we can seed a `nav_products.<slug>.benefits`
    array key.
*/
import React, { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Moon, Bed, Barbell, ShieldCheck, Sparkle, ArrowLeft, Clock,
    CheckCircle, HandHeart, Waveform, Certificate,
} from "@phosphor-icons/react";
import { useCms } from "@/lib/useCms";
import ProductEnquiryForm from "@/components/site/ProductEnquiryForm";


/* Slug (URL) → CMS key prefix, icon, palette, benefit chips. Keep in
   sync with /components/site/MegaMenu.jsx `PRODUCTS`. */
const PRODUCT_CONFIG = {
    "night-guard": {
        cmsKey: "night_guard",
        icon: Moon,
        heroGradient: "from-indigo-100 via-slate-100 to-white",
        iconTint: "bg-indigo-100 text-indigo-600",
        eyebrow: "For nightly bruxism & jaw tension",
        benefits: [
            { icon: ShieldCheck, label: "Enamel protection" },
            { icon: HandHeart, label: "Jaw-tension relief" },
            { icon: Sparkle, label: "Slim, custom-fit" },
            { icon: Certificate, label: "Doctor-supervised" },
        ],
    },
    "anti-snoring": {
        cmsKey: "anti_snoring",
        icon: Bed,
        heroGradient: "from-sky-100 via-cyan-100 to-white",
        iconTint: "bg-sky-100 text-sky-600",
        eyebrow: "For snoring & mild sleep-disordered breathing",
        benefits: [
            { icon: Waveform, label: "Opens the airway" },
            { icon: Clock, label: "Deeper, quieter sleep" },
            { icon: ShieldCheck, label: "MAD device, safe long-term" },
            { icon: Certificate, label: "Doctor-supervised" },
        ],
    },
    "sports-guard": {
        cmsKey: "sports_guard",
        icon: Barbell,
        heroGradient: "from-amber-100 via-rose-100 to-white",
        iconTint: "bg-amber-100 text-amber-600",
        eyebrow: "For contact sports & high-impact play",
        benefits: [
            { icon: ShieldCheck, label: "Shock-absorbing layers" },
            { icon: HandHeart, label: "Breathable, custom-fit" },
            { icon: Sparkle, label: "Elite comfort" },
            { icon: Certificate, label: "Doctor-supervised" },
        ],
    },
};


export default function ProductPage() {
    const { slug } = useParams();
    const cfg = PRODUCT_CONFIG[slug];
    const { t, loading } = useCms("nav_products");
    const title = t(`${cfg?.cmsKey}.title`, "");

    // Set document title for basic SEO once CMS resolves. Kept above
    // the early return so hook order stays stable across renders.
    useEffect(() => {
        if (title) document.title = `${title} · Invisique™`;
    }, [title]);

    // Route safety net — unknown slug bounces to Retainer as canonical
    // 4th product page (has full marketing content).
    if (!cfg) return <Navigate to="/infinite" replace />;

    const tagline = t(`${cfg.cmsKey}.tagline`, "");
    const blurb = t(`${cfg.cmsKey}.blurb`, "");
    const imageUrl = t(`${cfg.cmsKey}.image_url`, "");
    const ctaLabel = t(`${cfg.cmsKey}.cta_label`, "");
    const HeroIcon = cfg.icon;

    return (
        <div className="bg-white" data-testid={`product-page-${slug}`}>
            {/* ─── Hero ───────────────────────────────────────────── */}
            <section className={`relative overflow-hidden bg-gradient-to-br ${cfg.heroGradient}`}>
                <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-6 pb-4">
                    <Link
                        to="/"
                        data-testid={`product-page-back-${slug}`}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-primary transition-colors"
                    >
                        <ArrowLeft size={11} weight="bold" />
                        Back to Invisique
                    </Link>
                </div>

                <div className="max-w-7xl mx-auto px-5 sm:px-8 pb-16 lg:pb-24 grid lg:grid-cols-12 gap-8 items-start">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="lg:col-span-7"
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 backdrop-blur ring-1 ring-black/5 mb-4">
                            <div className={`w-6 h-6 rounded-md grid place-items-center ${cfg.iconTint}`}>
                                <HeroIcon size={14} weight="duotone" />
                            </div>
                            <span className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-foreground/80" data-testid={`product-page-eyebrow-${slug}`}>
                                {cfg.eyebrow}
                            </span>
                        </div>

                        <h1
                            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-foreground"
                            data-testid={`product-page-title-${slug}`}
                        >
                            {loading ? " " : (title || slug.replace(/-/g, " "))}
                        </h1>

                        {tagline && (
                            <div className="mt-3 text-lg sm:text-xl text-primary font-semibold" data-testid={`product-page-tagline-${slug}`}>
                                {tagline}
                            </div>
                        )}

                        {blurb && (
                            <p className="mt-4 text-base text-muted-foreground max-w-2xl leading-relaxed" data-testid={`product-page-blurb-${slug}`}>
                                {blurb}
                            </p>
                        )}

                        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid={`product-page-benefits-${slug}`}>
                            {cfg.benefits.map((b) => {
                                const Icon = b.icon;
                                return (
                                    <div
                                        key={b.label}
                                        className="bg-white/70 backdrop-blur border border-black/5 rounded-xl p-3 flex flex-col gap-1.5 items-start"
                                    >
                                        <Icon size={16} weight="duotone" className="text-primary" />
                                        <span className="text-[11.5px] font-semibold text-foreground leading-snug">
                                            {b.label}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        {imageUrl ? (
                            <div className="mt-8 rounded-2xl overflow-hidden ring-1 ring-black/5 shadow-lg max-w-xl" data-testid={`product-page-image-${slug}`}>
                                <img src={imageUrl} alt={title} className="w-full h-auto object-cover" />
                            </div>
                        ) : (
                            <div className="mt-8 rounded-2xl bg-gradient-to-br from-white to-primary/5 ring-1 ring-black/5 shadow-lg max-w-xl aspect-video grid place-items-center" data-testid={`product-page-image-placeholder-${slug}`}>
                                <div className="text-center">
                                    <HeroIcon size={48} weight="duotone" className={`${cfg.iconTint.split(" ")[1]} mx-auto`} />
                                    <div className="text-[11px] font-semibold text-muted-foreground mt-2">
                                        Product photo coming soon
                                    </div>
                                </div>
                            </div>
                        )}
                    </motion.div>

                    {/* ─── Enquiry form (sticky on wide) ────────── */}
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                        className="lg:col-span-5 lg:sticky lg:top-24"
                    >
                        <ProductEnquiryForm
                            productSlug={slug}
                            productTitle={title || slug.replace(/-/g, " ")}
                            ctaLabel={ctaLabel}
                        />

                        {/* Doctor-first note — reinforces the no-cart, no-shop rule
                            straight to the user. */}
                        <div className="mt-3 bg-secondary/40 border border-border rounded-xl p-3 text-[11px] text-muted-foreground leading-snug" data-testid={`product-page-note-${slug}`}>
                            <div className="flex items-start gap-2">
                                <CheckCircle size={12} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                                <span>
                                    <b className="text-foreground">Doctor-supervised only.</b> Every Invisique™ appliance is fitted through a certified provider — no direct-to-consumer shipping.
                                </span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ─── Trust strip ─────────────────────────────────────── */}
            <section className="border-y border-border bg-secondary/30">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 grid sm:grid-cols-3 gap-4 text-center sm:text-left">
                    <TrustPill icon={ShieldCheck} title="German-engineered" subtitle="Precision-fit for Indian anatomy" />
                    <TrustPill icon={Certificate} title="500+ partner clinics" subtitle="Fitted by certified providers only" />
                    <TrustPill icon={HandHeart} title="Personal callback" subtitle="A smile expert calls within minutes" />
                </div>
            </section>
        </div>
    );
}


function TrustPill({ icon: Icon, title, subtitle }) {
    return (
        <div className="flex items-center gap-3 justify-center sm:justify-start">
            <div className="w-9 h-9 rounded-lg bg-primary/10 grid place-items-center">
                <Icon size={16} weight="duotone" className="text-primary" />
            </div>
            <div>
                <div className="text-sm font-semibold text-foreground">{title}</div>
                <div className="text-[11px] text-muted-foreground">{subtitle}</div>
            </div>
        </div>
    );
}
